/**
 * Centralized timezone-aware date formatting using Intl.DateTimeFormat.
 * No external dependencies required.
 */

// ISO date-time with no timezone designator, e.g. "2026-09-29T20:05:08.515784".
// The backend serializes java.time.LocalDateTime without an offset, and the wall
// clock it stores is UTC. ES2015+ parses such strings as browser-LOCAL time, so
// without this they are off by the viewer's UTC offset.
const NAIVE_DATETIME_RE = /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}(:\d{2}(\.\d+)?)?$/;

function toDate(dateInput) {
  if (!dateInput) return null;
  if (dateInput instanceof Date) return dateInput;
  let value = dateInput;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    // Date-only strings ("2026-09-29") are already parsed as UTC midnight - leave them.
    if (NAIVE_DATETIME_RE.test(trimmed)) {
      value = `${trimmed.replace(' ', 'T')}Z`;
    } else {
      value = trimmed;
    }
  }
  const d = new Date(value);
  return isNaN(d.getTime()) ? null : d;
}

/**
 * Format a date according to the user's chosen date format and company timezone.
 * @param {string|Date} dateInput
 * @param {string} dateFormat - 'MM/DD/YYYY', 'DD/MM/YYYY', or 'YYYY-MM-DD'
 * @param {string} timezone - IANA timezone e.g. 'America/New_York'
 * @returns {string}
 */
export function formatDate(dateInput, dateFormat = 'MM/DD/YYYY', timezone) {
  const d = toDate(dateInput);
  if (!d) return '';

  const options = { timeZone: timezone || undefined };
  const parts = new Intl.DateTimeFormat('en-US', {
    ...options,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(d);

  const p = {};
  parts.forEach(({ type, value }) => { p[type] = value; });

  switch (dateFormat) {
    case 'DD/MM/YYYY':
      return `${p.day}/${p.month}/${p.year}`;
    case 'YYYY-MM-DD':
      return `${p.year}-${p.month}-${p.day}`;
    case 'MM/DD/YYYY':
    default:
      return `${p.month}/${p.day}/${p.year}`;
  }
}

/**
 * Format a DATE-ONLY value (backend java.time.LocalDate, e.g. "2026-09-29").
 * Pinned to UTC so the calendar date never drifts by the viewer's offset.
 * @param {string|Date} dateInput
 * @param {string} dateFormat
 * @returns {string}
 */
export function formatDateOnly(dateInput, dateFormat = 'MM/DD/YYYY') {
  return formatDate(dateInput, dateFormat, 'UTC');
}

/**
 * Format only the time portion: "8:05 PM"
 * @param {string|Date} dateInput
 * @param {string} timezone
 * @returns {string}
 */
export function formatTime(dateInput, timezone) {
  const d = toDate(dateInput);
  if (!d) return '';

  return new Intl.DateTimeFormat('en-US', {
    timeZone: timezone || undefined,
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).format(d);
}

/**
 * Format a date with time.
 * @param {string|Date} dateInput
 * @param {string} dateFormat
 * @param {string} timezone
 * @returns {string}
 */
export function formatDateTime(dateInput, dateFormat = 'MM/DD/YYYY', timezone) {
  const d = toDate(dateInput);
  if (!d) return '';

  return `${formatDate(d, dateFormat, timezone)} ${formatTime(d, timezone)}`;
}

/**
 * Format a date as relative time ("5m ago", "2h ago") falling back to formatDate.
 * @param {string|Date} dateInput
 * @param {string} dateFormat
 * @param {string} timezone
 * @returns {string}
 */
export function formatRelativeTime(dateInput, dateFormat = 'MM/DD/YYYY', timezone, labels) {
  const d = toDate(dateInput);
  if (!d) return '';

  const now = new Date();
  const diffMs = now - d;
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHr = Math.floor(diffMin / 60);
  const diffDays = Math.floor(diffHr / 24);

  // `labels` lets a caller supply translated strings; the English forms are the default
  // so the existing call sites keep working unchanged.
  if (diffSec < 60) return labels?.justNow?.() ?? 'Just now';
  if (diffMin < 60) return labels?.minutesAgo?.(diffMin) ?? `${diffMin}m ago`;
  if (diffHr < 24) return labels?.hoursAgo?.(diffHr) ?? `${diffHr}h ago`;
  if (diffDays < 7) return labels?.daysAgo?.(diffDays) ?? `${diffDays}d ago`;

  return formatDate(d, dateFormat, timezone);
}

/**
 * Format a date in short readable form: "Jan 15, 2025"
 * @param {string|Date} dateInput
 * @param {string} timezone
 * @returns {string}
 */
export function formatDateShort(dateInput, timezone) {
  const d = toDate(dateInput);
  if (!d) return '';

  return new Intl.DateTimeFormat('en-US', {
    timeZone: timezone || undefined,
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(d);
}
